import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserDetails, getS3Config } from "../../api/apiHandler";
import './ChartPage.css';
import { FMPDatafeed } from '../../api/fmp/FMPDatafeed.js';

const ChartPage = () => {
  const { user_id, type, symbol } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartContainerRef = useRef();
  const chartRef = useRef(null);
  const widgetRef = useRef(null);
  const [chartWidgetState, setChartWidgetState] = useState(null);
  const [SYMBOL, SET_SYMBOL] = useState(null); // Bot symbol state
  const [INTERVAL, SET_INTERVAL] = useState(null); // Bot interval state
  const [apiKey, setApiKey] = useState(null); // Bot interval state
  const [userprofile, setUserProfile] = useState(null); // Bot interval state

  useEffect(() => {
    // Add this flag to prevent double execution in React StrictMode
    let isMounted = true;

    async function fetchData() {
      const confResponse = await getS3Config({});
      if (parseInt(confResponse.code) === 1 && isMounted) {
        setApiKey(confResponse?.data?.REACT_APP_FMP_API_KEY);
      }

      const userReq = {
        user_id: user_id,
        is_token: true
      }
      const response = await getUserDetails(userReq);
      if (parseInt(response.code) === 1 && isMounted) {
        setUserProfile(response.data);
      } else {
        console.error(response.message);
      }
    }
            
    fetchData();

    // Validate and set symbol
    if (symbol) {
      const cleanSymbol = symbol.trim().toUpperCase();
      if (cleanSymbol && isMounted) {
        SET_SYMBOL(cleanSymbol);
        SET_INTERVAL('1');
        setIsLoading(false);
      } else {
        if (isMounted) {
          setError('Invalid symbol provided');
          setIsLoading(false);
        }
      }
    } else {
      if (isMounted) {
        setError('No symbol provided');
        setIsLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [symbol, user_id]); // Add dependencies

  useEffect(() => {
    if (!SYMBOL || !INTERVAL || !userprofile) return; // Wait until SYMBOL and INTERVAL are set
    if (chartContainerRef.current) {
      // console.log("Initializing TradingView widget with:", { SYMBOL, INTERVAL, userprofile });
      const widgetOptions = {
        symbol: SYMBOL.toUpperCase(), // e.g., 'BTCUSDT'
        datafeed: new FMPDatafeed({ 
          type: (type || 'STOCK').toUpperCase(),
          dxUsername: userprofile?.dx_username || '',
          dxPassword: userprofile?.original_password || '',
          dxClearingCode: userprofile?.clearing_code || '',
          dxSessionToken: userprofile?.sessionToken || '',
          apiKey: apiKey || ''
        }),
        interval: INTERVAL,
        container: chartContainerRef.current,
        library_path: '/assets/charting/charting_library/',
        locale: 'en',
        disabled_features: [
          'use_localstorage_for_settings',
          'header_symbol_search',
          'header_chart_type',
          'header_compare',
          'header_undo_redo',
          'symbol_search_hot_key',
          'header_screenshot',
          'adaptive_logo',
          'volume_force_overlay',
          'create_volume_indicator_by_default',
          'show_volume_by_default',
          'header_resolutions',
          'header_settings',
          'header_save_button',
          'header_save_to_cloud_button',
          'footer_timestamp',
          // Add extra
          'timezone_menu',
          'go_to_date',
          'pricescale_unit',
          'hide_resolution_in_legend',
          'scales_time_hours_format',
          'show_percent_option_for_right_margin'
        ],
        enabled_features: [
          "pre_post_market_sessions",
          'always_show_legend_values_on_mobile'
        ],
        charts_storage_url: 'https://saveload.tradingview.com',
        charts_storage_api_version: '1.1',
        client_id: 'alchemy_trading_app',
        user_id: 'public_user',
        fullscreen: false,
        autosize: true,
        intraday_multipliers: ['1', '5', '15', '30', '60'],
        inactivegaps: true,
        has_empty_bars: true,
        studies_overrides: {},
        theme: 'Light',
        overrides: {
          // Chart type and style
          'mainSeriesProperties.style': 1,       // 0 for candles
          'mainSeriesProperties.showCountdown': false,
          // Price formatting for scale and OHLC values
          'symbolWatermarkProperties.transparency': 90,
          'scalesProperties.priceFormat.type': 'price',
          'scalesProperties.priceFormat.precision': 0, // 0 decimal places for rounding
          
          // You can also use custom formatter
          'mainSeriesProperties.priceFormat': {
            type: 'price',
            precision: 0,
            minMove: 1
          },
          // ADD THESE:
          'paneProperties.legendProperties.showSeriesTitle': false,
          'paneProperties.legendProperties.showStudyTitles': false,
          'paneProperties.legendProperties.showLegend': false,
        },
        // Add this separate configuration
        custom_formatters: {
          priceFormatterFactory: () => {
            return {
              format: (price) => {
                // Round to nearest integer and add thousand separators
                // return Math.round(price).toLocaleString();
                return price.toFixed(2).toLocaleString();
              }
            };
          },
          // For status line (OHLC values at top)
          statusLineFormatter: (price) => {
            // return Math.round(price).toLocaleString();
            return price.toFixed(2).toLocaleString();
          }
        },
        time_frames: [
          { text: "1D", resolution: "1" },
          { text: "5D", resolution: "1" },
          { text: "1M", resolution: "60" },
          { text: "3M", resolution: "240" },
          { text: "6M", resolution: "1D" },
          { text: "1Y", resolution: "1D" },
          { text: "5Y", resolution: "1D" }
        ]
      };

      if (!widgetRef.current) {
        // Create widget only if it doesn't exist
        // eslint-disable-next-line no-undef
        widgetRef.current = new TradingView.widget(widgetOptions);
        chartRef.current = widgetRef.current;
        // Ensure React state updates so children receive the widget instance
        try {
          setChartWidgetState(widgetRef.current);
        } catch (e) {
          console.warn('Could not set chartWidgetState after creation', e);
        }
      } else {
        // Update existing widget
        widgetRef.current.setSymbol(SYMBOL.toUpperCase(), INTERVAL);
        // Sync React state in case widgetRef.current changed
        try {
          setChartWidgetState(widgetRef.current);
        } catch (e) {
          console.warn('Could not set chartWidgetState after update', e);
        }
      }
    }
    
    return () => {
      if (widgetRef.current) {
        // Clean up widget if component unmounts
        try {
          widgetRef.current.remove();
          widgetRef.current = null;
          chartRef.current = null;
          setChartWidgetState(null);
        } catch (e) {
          console.error('Error cleaning up TradingView widget:', e);
        }
      }
    };
  }, [SYMBOL, INTERVAL, userprofile]);

  if (isLoading) {
    return (
      <div className="chart-page loading">
        <div className="loader"></div>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-page error">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-wrapper">
      <div style={{ position: 'relative' }}>
        <div ref={chartContainerRef} className="chart-container"></div>
      </div>
    </div>
  );
};

export default ChartPage;